import { Typography } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  LabelList,
  Cell,
  Text,
} from "recharts";
import VizHeader from "../VizHeader/VizHeader";
import { Tooltip as Tooltip2 } from "@mui/material";

export default function CurationStatusBreakdownExpertOverview() {
  const expertOverview = useSelector((state) => state.expertOverviewCmReducer);
  const [activeKeys, setActiveKeys] = React.useState([
    "High",
    "Medium",
    "Low",
    "Curated",
  ]);
  const [activeLegend, setActiveLegend] = React.useState(null);
  const [getTooltipPayload, setTooltipPayload] = React.useState([]);
  const tickFormatter = (value) => {
    const limit = 10;
    if (value.length < limit) return value;
    else return `${value.substring(0, limit)}...`;
  };
  const truncate = (str, max = 8) => {
    if (!str) return "";
    return str.length > max ? str.substring(0, max) + "…" : str;
  };
  const CustomTick = ({ x, y, payload }) => {
    const fullName = payload?.value || "";
    const parts = fullName.split(" ");
    const first = parts[0] || "";
    const last = parts.slice(1).join(" "); // supports middle names too

    // value shown in the axis (first line + second line)
    const displayValue = last ? `${first}\n${last}` : first;
    console.log(displayValue);
    return (
      <Tooltip2 title={fullName} placement="top">
        <g transform={`translate(${x},${y})`}>
          <Text
            x={0}
            y={0}
            textAnchor="end"
            verticalAnchor="start"
            angle={-35} // slanted
            fontSize={11}
          >
            {truncate(fullName, 16)}
          </Text>
        </g>
      </Tooltip2>
    );
  };
  const onHandleMouseOver = (data) => {
    setActiveLegend(data.dataKey);
  };

  const onHandleMouseOut = () => {
    setActiveLegend(null);
  };

  const handleBarMouseEnter = (data) => {
    setTooltipPayload(data?.tooltipPayload);
  };

  const handleBarMouseLeave = () => {
    setTooltipPayload([]);
  };

  const handleLegendClick = (e) => {
    const { dataKey } = e;

    // Check if the dataKey is already active
    const isActive = activeKeys.includes(dataKey);

    // If active, remove it from activeKeys; if not active, add it
    setActiveKeys((prevActiveKeys) =>
      isActive
        ? prevActiveKeys.filter((key) => key !== dataKey)
        : [...prevActiveKeys, dataKey]
    );
  };

  const CustomTooltip = ({ label, payload, active }) => {
    if (
      active &&
      payload &&
      payload.length > 0 &&
      getTooltipPayload?.length > 0
    ) {
      return (
        <p
          className="custom-tooltip"
          style={{
            backgroundColor: "#fff",
            padding: "5px",
            border: `1px solid ${getTooltipPayload[0]?.color}`,
          }}
        >
          <>
            {getTooltipPayload?.map((obj, ind) => {
              if (obj?.value > 0) {
                return (
                  <>
                    <p className="label">{obj?.payload?.name}</p>
                    <p className="label">
                      Total: {obj?.payload?.totalCount.toLocaleString()}
                    </p>
                    <p className="label" style={{ color: obj?.color }}>
                      {obj?.name}: {obj?.value?.toLocaleString()}
                    </p>
                  </>
                );
              }
            })}
          </>
        </p>
      );
    }
  };

  return (
    <>
      <div style={{ textAlign: "center", paddingTop: "10px" }}>
        <VizHeader type={4} isBold={true}>
          {" "}
          Curation Status Breakdown - Top 20{" "}
        </VizHeader>
      </div>
      <ResponsiveContainer width={"100%"} height={400}>
        <BarChart
          data={expertOverview.getCurationCount}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 40,
          }}
          barSize={40}
        >
          <CartesianGrid stroke="#ececec" vertical={false} />
          <XAxis
            tick={<CustomTick />}
            dataKey="name"
            interval={0}
            fontSize={12}
            angle={-45}
            dy={35}
            height={70}
          >
            <Label
              value="Experts"
              offset={15}
              dy={45}
              fontSize={12}
              fontWeight={"bold"}
              position="outsideBottom"
            />
          </XAxis>
          <YAxis
            axisLine={false}
            tickLine={false}
            label={{
              dx: -20,
              value: "Question Volume",
              angle: -90,
              position: "insideCenter",
              fontSize: "12px",
              fontWeight: "bold",
            }}
            fontSize={12}
          />
          <Tooltip
            content={<CustomTooltip />}
            wrapperStyle={{ fontSize: "12px", fontWeight: "bold" }}
            cursor={{ fill: "transparent" }}
          />
          <Legend
            onMouseEnter={onHandleMouseOver}
            onMouseLeave={onHandleMouseOut}
            margin={{ bottom: 5 }}
            onClick={handleLegendClick}
            verticalAlign="top"
            align="right"
            layout="vertical"
            wrapperStyle={{
              fontSize: "12px",
              paddingTop: "12px",
              paddingBottom: "7px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          />
          <Bar
            onMouseEnter={handleBarMouseEnter}
            onMouseLeave={handleBarMouseLeave}
            dataKey="High"
            stackId="a"
            fill="#ef7928"
            hide={!activeKeys.includes("High")}
            opacity={activeLegend === "High" || activeLegend === null ? 1 : 0.2}
          />
          <Bar
            onMouseEnter={handleBarMouseEnter}
            onMouseLeave={handleBarMouseLeave}
            dataKey="Medium"
            stackId="a"
            fill="#f4c900"
            hide={!activeKeys.includes("Medium")}
            opacity={
              activeLegend === "Medium" || activeLegend === null ? 1 : 0.2
            }
          />
          <Bar
            onMouseEnter={handleBarMouseEnter}
            onMouseLeave={handleBarMouseLeave}
            dataKey="Low"
            stackId="a"
            fill="#00c16e"
            hide={!activeKeys.includes("Low")}
            opacity={activeLegend === "Low" || activeLegend === null ? 1 : 0.2}
          />
          <Bar
            onMouseEnter={handleBarMouseEnter}
            onMouseLeave={handleBarMouseLeave}
            dataKey="Curated"
            stackId="a"
            fill="#009BFF"
            hide={!activeKeys.includes("Curated")}
            opacity={
              activeLegend === "Curated" || activeLegend === null ? 1 : 0.2
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}
